import React, { useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import { formatTimestamp } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderDocumentTextWithHighlight = (text = "", highlightText = "") => {
  if (!highlightText) {
    return text;
  }

  const exactIndex = text.indexOf(highlightText);
  if (exactIndex >= 0) {
    const before = text.slice(0, exactIndex);
    const match = text.slice(exactIndex, exactIndex + highlightText.length);
    const after = text.slice(exactIndex + highlightText.length);
    return (
      <>
        {before}
        <mark className="rounded-md bg-fuchsia-500/25 px-1.5 py-0.5 text-foreground ring-1 ring-fuchsia-500/40">
          {match}
        </mark>
        {after}
      </>
    );
  }

  const regex = new RegExp(`(${escapeRegExp(highlightText)})`, "i");
  const parts = text.split(regex);
  if (parts.length <= 1) {
    return text;
  }

  return parts.map((part, idx) => (
    regex.test(part) ? (
      <mark key={`${part}-${idx}`} className="rounded-md bg-fuchsia-500/25 px-1.5 py-0.5 text-foreground ring-1 ring-fuchsia-500/40">
        {part}
      </mark>
    ) : (
      <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>
    )
  ));
};

const TranscriptViewer = forwardRef(({
  transcript,
  segments,
  highlightedSegment,
  highlightedExcerpt = "",
  isDocument = false,
  speakerProfiles = [],
  language = "es",
  onEditSpeakerSegment,
  onEditTranscriptSegment,
}, ref) => {
  const segmentRefs = useRef({});
  const speakerLookup = Object.fromEntries((speakerProfiles || []).map((profile) => [
    profile.speaker_id,
    profile.custom_name || profile.suggested_name || profile.label || profile.speaker_id,
  ]));

  // Expose scrollToSegment method to parent
  useImperativeHandle(ref, () => ({
    scrollToSegment: (idx) => {
      const element = segmentRefs.current[idx];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }));

  // Auto-scroll when highlighted segment changes
  useEffect(() => {
    if (highlightedSegment !== null && segmentRefs.current[highlightedSegment]) {
      segmentRefs.current[highlightedSegment].scrollIntoView({ 
        behavior: "smooth", 
        block: "center" 
      });
    }
  }, [highlightedSegment]);

  const getSpeakerDisplayName = (segment) => {
    if (!segment?.speaker_id) return null;
    return speakerLookup[segment.speaker_id] || segment.speaker_label || (language === "es" ? "Speaker" : "Speaker");
  };

  if (!transcript) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No transcript available
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 lg:p-6 xl:p-7" data-testid="transcript-viewer">
      {segments && segments.length > 0 ? (
        <div className="space-y-4">
          {segments.map((segment, idx) => (
            <div 
              key={idx} 
              ref={(el) => segmentRefs.current[idx] = el}
              className={`group flex ${isDocument ? "gap-3" : "gap-4 lg:gap-5"} rounded-xl p-3 lg:p-4 transition-all duration-300 ${
                highlightedSegment === idx
                  ? (isDocument
                      ? "border border-fuchsia-500/45 bg-muted/10 shadow-[0_0_0_1px_rgba(217,70,239,0.12)]"
                      : "bg-primary/20 border border-primary/50")
                  : "hover:bg-muted/50"
              }`}
              data-testid={`segment-${idx}`}
            >
              {isDocument ? (
                <div
                  className={`mt-0.5 shrink-0 rounded-xl border px-2 py-2 text-[11px] font-medium leading-tight text-center ${
                    highlightedSegment === idx
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border/70 bg-muted/30 text-muted-foreground"
                  }`}
                  style={{ width: "88px" }}
                  data-testid={`document-segment-label-${idx}`}
                >
                  {segment.label || formatTimestamp(segment.start)}
                </div>
              ) : (
                <span className={`timestamp-pill mt-0.5 shrink-0 text-sm ${
                  highlightedSegment === idx ? "bg-primary text-primary-foreground" : ""
                }`}>
                  {segment.label || formatTimestamp(segment.start)}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  {getSpeakerDisplayName(segment) ? (
                    <div className="flex items-center gap-2" data-testid={`transcript-speaker-${idx}`}>
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
                      {getSpeakerDisplayName(segment)}
                    </span>
                    {onEditSpeakerSegment ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 rounded-full px-2 text-[11px]"
                        onClick={() => onEditSpeakerSegment(idx, segment)}
                        data-testid={`transcript-edit-speaker-${idx}`}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        {language === "es" ? "Corregir" : "Fix"}
                      </Button>
                    ) : null}
                    </div>
                  ) : <span />}
                  {onEditTranscriptSegment && !segment?.is_system_marker ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 rounded-full px-2 text-[11px]"
                      onClick={() => onEditTranscriptSegment(idx, segment)}
                      data-testid={`transcript-edit-segment-${idx}`}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      {language === "es" ? "Editar texto" : "Edit text"}
                    </Button>
                  ) : null}
                </div>
                <p className="text-base leading-8 text-foreground sm:text-[1.05rem] lg:text-[1.08rem] lg:leading-9 xl:text-[1.12rem]">
                  {isDocument && highlightedSegment === idx && highlightedExcerpt
                    ? renderDocumentTextWithHighlight(segment.text, highlightedExcerpt)
                    : segment.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-base leading-8 text-foreground sm:text-[1.05rem] lg:text-[1.08rem] lg:leading-9 xl:text-[1.12rem]">
          {transcript}
        </p>
      )}
    </div>
  );
});

TranscriptViewer.displayName = "TranscriptViewer";

export default TranscriptViewer;
